import { useState } from 'react'
import type { PropsWithChildren } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { ContractTaxonomyFragment } from 'generated/resources/graphql'
import type moment from 'moment'

import { observer } from 'mobx-react'

import { useMemo } from 'modules/core/hooks/useMemo'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import SupplementForm from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/SupplementForm'
import type SupplementsFormStore from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/store'
import { fromToParams, range } from 'modules/payroll_bundle/components/Handoffs/dates'
import useFetchSupplementInsights from 'modules/payroll_bundle/hooks/useFetchSupplementInsights'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import draftSupplementsCollection from 'modules/payroll_bundle/store/collections/draftSupplements'
import supplementsCollection, {
  Supplements,
} from 'modules/payroll_bundle/store/collections/supplements'
import Supplement from 'modules/payroll_bundle/store/models/Supplement'

import FormComponent from './Form'

interface LineProps {
  isLast: boolean
}

const Line = ({ isLast, children }: PropsWithChildren<LineProps>) => (
  <Box paddingBottom={isLast ? 's0' : 's12'}>{children}</Box>
)

export const checkAndProcessForm = async (
  form: SupplementForm,
  policyPeriodId: string,
  deleteSupplementByContract: () => Promise<void>
) => {
  if (!form.get('contracts_taxonomy_id').value) return Promise.resolve()

  // If the amount of an editable supplement by contract (i.e. linked to a
  // compensation) that was persisted (it has an id) has been deleted and
  // left blank by the user, then it deletes the supplement (so it will be
  // returned again by GET /payroll/draft_supplements instead of
  // GET /handoffs/supplements)
  if (
    form.supplementData.id &&
    form.get('contracts_compensation_id').value &&
    !form.get('amount_in_cents').value
  ) {
    await deleteSupplementByContract()
    return
  }

  if (!form.isValid) return Promise.resolve()

  if (policyPeriodId && !form.get('payroll_policy_period_id').value) {
    form.get('payroll_policy_period_id').set(policyPeriodId)
  }
}

export const deleteSupplementAndUpdateForm = async (
  form: SupplementForm,
  fetchSupplements: () => Promise<[unknown, unknown]>,
  store: SupplementsFormStore
) => {
  const supplement = new Supplement(form.supplementData)
  await supplement.delete()
  await fetchSupplements()

  const contractsCompensationId = form.data().contracts_compensation_id

  const draftSupplement = draftSupplementsCollection.find({
    contracts_compensation_id: contractsCompensationId,
  })
  store.replaceForm(form, new SupplementForm(draftSupplement?.toJS() ?? {}))
}

export const fetchSupplementsWithParams = (
  policyPeriodId: string | null,
  date: moment.Moment,
  frequency: string
) => {
  let supplementData
  let draftSupplementsData

  if (policyPeriodId) {
    supplementData = { policy_period_ids: [policyPeriodId] }
    draftSupplementsData = { payroll_policy_period_id: policyPeriodId }
  } else {
    const frequencyData = {
      ...fromToParams(range(date, frequency)),
      frequency,
    }
    supplementData = frequencyData
    draftSupplementsData = frequencyData
  }

  return Promise.all([
    supplementsCollection.fetch({ data: supplementData }),
    draftSupplementsCollection.fetch({ data: draftSupplementsData }),
  ])
}

export const createOrUpdateSupplementForm = async (
  form: SupplementForm,
  setIsCreating: (isCreating: boolean) => void,
  fetchInsightsData: () => Promise<void>,
  isCreating: boolean
): Promise<SupplementForm> => {
  const formData = form.supplementData

  const isCreate = !formData.id
  if (isCreate && isCreating) return form

  const supplement: any = formData.id ? new Supplement(formData) : new Supplements([formData])

  setIsCreating(true)
  const { success } = await asyncAction(
    () => (isCreate ? form.create(supplement) : form.save(supplement)),
    { successMessage: i18n.t('payroll.period.variable_compensations.success') }
  )
  setIsCreating(false)

  if (!success) return form

  if (isCreate) {
    track(trackingEvents.payroll.createSupplement)
  } else {
    track(trackingEvents.payroll.updateSupplement)
  }

  fetchInsightsData()

  return form
}

interface Props {
  form: SupplementForm
  store: SupplementsFormStore
  taxonomies: ContractTaxonomyFragment[]
  itemToHighlight?: { supplementId: string } | null
  readonly: boolean
  isLast: boolean
  date: moment.Moment
  frequency: string
  hover: boolean
  toggleTaxonomyCreationModal: (value: boolean) => void
}

const SupplementFormComponent = ({
  date,
  form,
  store,
  taxonomies,
  itemToHighlight,
  isLast,
  frequency,
  readonly = false,
  hover,
  toggleTaxonomyCreationModal,
}: Props) => {
  const { currentPolicyPeriods, periodIndex } = usePolicyPeriods()
  const { fetchInsightsData } = useFetchSupplementInsights({
    triggerFetch: false,
  })
  const [isCreating, setIsCreating] = useState(false)

  const fetchSupplements = () => {
    return fetchSupplementsWithParams(policyPeriodId, date, frequency)
  }

  const deleteSupplementByContract = async () => {
    await deleteSupplementAndUpdateForm(form, fetchSupplements, store)
  }

  const policyPeriodId = useMemo(
    () => (currentPolicyPeriods.length > 0 ? currentPolicyPeriods[periodIndex]?.id : null), // eslint-disable-next-line react-hooks/exhaustive-deps
    [periodIndex]
  )

  const onSubmit = async () => {
    await checkAndProcessForm(form, policyPeriodId, deleteSupplementByContract)
    await asyncAction(() => createOrUpdateForm(form), {
      successFlash: false,
    })
  }

  const createOrUpdateForm = async (form: SupplementForm): Promise<SupplementForm> => {
    await createOrUpdateSupplementForm(form, setIsCreating, fetchInsightsData, isCreating)

    return form
  }

  const onDelete = async () => {
    const supplement = new Supplement(form.supplementData)

    await asyncAction(
      () => {
        if (supplement.isNew) {
          store.removeForm(form)
          return Promise.resolve()
        }

        // FIXME: Consider handling errors and not removing the form if something fails
        store.removeForm(form)
        return Promise.all([supplement.delete(), fetchSupplements()])
      },
      {
        successMessage: i18n.t('payroll.period.variable_compensations.delete_success'),
      }
    )
  }

  return (
    <Line isLast={isLast}>
      <FormComponent
        form={form}
        store={store}
        taxonomies={taxonomies}
        itemToHighlight={itemToHighlight}
        readonly={readonly}
        onSubmit={onSubmit}
        onDelete={onDelete}
        hover={hover}
        toggleTaxonomyCreationModal={toggleTaxonomyCreationModal}
      />
    </Line>
  )
}

export default observer(SupplementFormComponent)
